import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Button, Avatar } from "@mui/material";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import Loader from "../../components/Loader";
import { tokens } from "../../theme";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";

const mono = '"IBM Plex Mono", monospace';
const disp = '"Space Grotesk", sans-serif';

const fmtSc = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// One color per segment card, cycled by position.
const palette = ["#8b6df0", "#23d6a0", "#4b9fe1", "#f5a623", "#f25c66", "#e879c7", "#9aa7c7"];

export default function Segments() {
  const navigate = useNavigate();
  const [segments, setSegments] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchSegments = async () => {
      setIsLoading(true);
      try {
        const response = await getAllData(endPoints.api.GET_SEGMENTS);
        if (response?.status === "success") {
          // Each player type comes back as {id, name, count, total_sc_credit, total_sc_main}.
          setSegments(
            (response.data || []).map((s, i) => ({
              id: s.id,
              name: s.name,
              count: Number(s.count) || 0,
              scCredit: Number(s.total_sc_credit) || 0,
              scMain: Number(s.total_sc_main) || 0,
              color: palette[i % palette.length],
              def: `player_type = '${s.name}'`,
            }))
          );
        }
      } catch (e) {
        console.error("segments fetch failed", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSegments();
  }, []);

  return (
    <Box>
      {isLoading && <Loader spinner={true} visible={isLoading} />}
      <PageHeader
        title="Segments"
        crumb="Audience / Segments"
        subtitle="Player types as reusable audiences for targeting offers and campaigns"
        actions={<Button variant="contained" startIcon={<AddRoundedIcon />}>New segment</Button>}
      />

      <Grid container spacing={1.75}>
        {segments.length === 0 && !isLoading && (
          <Grid item xs={12}>
            <SectionCard>
              <Typography sx={{ color: tokens.textDim, fontSize: 13 }}>No segments found</Typography>
            </SectionCard>
          </Grid>
        )}
        {segments.map((s) => (
          <Grid item xs={12} sm={6} lg={4} key={s.id}>
            <SectionCard sx={{ height: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Avatar variant="rounded" sx={{ width: 42, height: 42, borderRadius: "11px", bgcolor: `${s.color}22`, color: s.color }}>
                  <GroupRoundedIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: disp, fontSize: 14.5, fontWeight: 600 }}>{s.name}</Typography>
                  <Typography sx={{ fontFamily: disp, fontSize: 26, fontWeight: 600, color: s.color, mt: 0.3 }}>
                    {s.count.toLocaleString()}
                  </Typography>
                  <Typography sx={{ color: tokens.textDim, fontSize: 11 }}>players</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                {[
                  { label: "SC Play", value: s.scCredit },
                  { label: "SC Redeem", value: s.scMain },
                ].map((m) => (
                  <Box key={m.label} sx={{ flex: 1, p: 1, borderRadius: "8px", border: `1px solid ${tokens.border}` }}>
                    <Typography sx={{ color: tokens.textDim, fontSize: 11 }}>{m.label}</Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: 14, fontWeight: 600 }}>{fmtSc(m.value)}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 1.5, p: 1, borderRadius: "8px", background: tokens.bg2, border: `1px solid ${tokens.border}` }}>
                <Typography sx={{ fontFamily: mono, fontSize: 11, color: tokens.textDim }}>{s.def}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate("/UserList", { state: { player_type: s.id } })}
                >
                  View players
                </Button>
                <Button size="small" variant="contained">Target offer</Button>
              </Box>
            </SectionCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
